import { useState } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AppSettingsProvider, useAppSettings } from './contexts/AppSettingsContext';
import { Login } from './components/Auth/Login';
import { Register } from './components/Auth/Register';
import { Navigation } from './components/Layout/Navigation';
import { Home } from './components/Dashboard/Home';
import { Discounts } from './components/Pages/Discounts';
import { Events } from './components/Pages/Events';
import { Jobs } from './components/Pages/Jobs';
import { Profile } from './components/Pages/Profile';
import { DigitalID } from './components/Pages/DigitalID';
import { VendorPost } from './components/Pages/VendorPost';
import { BusinessProfile } from './components/Pages/BusinessProfile';
import { Admin } from './components/Admin/Admin';
import { XCircle } from 'lucide-react';

function AuthenticatedApp() {
  const [currentPage, setCurrentPage] = useState('home');
  const { settings } = useAppSettings();

  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return <Home onNavigate={setCurrentPage} />;
      case 'discounts':
        if (!settings?.discounts_enabled) {
          return <FeatureDisabled feature="Discounts" onNavigate={setCurrentPage} />;
        }
        return <Discounts />;
      case 'events':
        if (!settings?.events_enabled) {
          return <FeatureDisabled feature="Events" onNavigate={setCurrentPage} />;
        }
        return <Events />;
      case 'jobs':
        if (!settings?.jobs_enabled) {
          return <FeatureDisabled feature="Jobs" onNavigate={setCurrentPage} />;
        }
        return <Jobs />;
      case 'profile':
        return <Profile />;
      case 'digital-id':
        return <DigitalID />;
      case 'business-profile':
        return <BusinessProfile onBack={() => setCurrentPage('home')} />;
      case 'vendor-post':
        return <VendorPost />;
      case 'admin':
        return <Admin />;
      default:
        return <Home onNavigate={setCurrentPage} />;
    }
  };

  return (
    <div className="min-h-screen pb-20 md:pb-0">
      {currentPage !== 'admin' && (
        <Navigation currentPage={currentPage} onNavigate={setCurrentPage} />
      )}
      <div className="page-transition">
        {renderPage()}
      </div>
    </div>
  );
}

function FeatureDisabled({ feature, onNavigate }: { feature: string; onNavigate: (page: string) => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full mb-6">
          <XCircle className="text-gray-400 dark:text-gray-500" size={48} />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
          Feature Unavailable
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-8 text-lg">
          The {feature} feature is currently disabled.
        </p>
        <button
          onClick={() => onNavigate('home')}
          className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
        >
          Return to Home
        </button>
      </div>
    </div>
  );
}

function AppContent() {
  const { user, loading } = useAuth();
  const [showRegister, setShowRegister] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400 text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return showRegister ? (
      <Register onToggle={() => setShowRegister(false)} />
    ) : (
      <Login onToggle={() => setShowRegister(true)} />
    );
  }

  return <AuthenticatedApp />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppSettingsProvider>
          <AppContent />
        </AppSettingsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
